// components/reports/ReportChart.jsx
import React from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useTheme } from "../contexts/ThemeContext";

const COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
  "#14B8A6",
  "#F97316",
];

const ReportChart = ({
  data,
  chartType,
  chartConfig,
  columns,
  groupings,
  aggregates,
}) => {
  const { isDarkMode } = useTheme();

  if (!data || data.length === 0) {
    return (
      <div
        className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        No data to display
      </div>
    );
  }

  // Transform data for charting
  const transformDataForChart = () => {
    if (groupings && groupings.length > 0) {
      // Grouped data
      return data.map((row) => {
        const transformed = {
          name: row[groupings[0].field] || "Unknown",
        };

        aggregates?.forEach((agg) => {
          const key = agg.label || `${agg.function}(${agg.field})`;
          transformed[key] = parseFloat(row[key]) || 0;
        });

        return transformed;
      });
    } else {
      // Non-grouped data - use first column as label
      return data.slice(0, 20).map((row, index) => {
        const transformed = {
          name: row[columns[0]?.field] || `Item ${index + 1}`,
        };

        columns.slice(1).forEach((col) => {
          const value = row[col.field];
          transformed[col.label] = parseFloat(value) || 0;
        });

        return transformed;
      });
    }
  };

  const chartData = transformDataForChart();

  // Get data keys (metric names)
  const dataKeys =
    chartData.length > 0
      ? Object.keys(chartData[0]).filter((k) => k !== "name")
      : [];

  const tooltipStyle = {
    backgroundColor: isDarkMode ? "#1F2937" : "#FFFFFF",
    border: isDarkMode ? "1px solid #374151" : "1px solid #E5E7EB",
    borderRadius: "0.5rem",
    color: isDarkMode ? "#FFF" : "#1F2937",
  };

  const axisStroke = isDarkMode ? "#9CA3AF" : "#6B7280";
  const gridStroke = isDarkMode ? "#374151" : "#E5E7EB";

  const renderChart = () => {
    switch (chartType) {
      case "bar":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis dataKey="name" stroke={axisStroke} />
              <YAxis stroke={axisStroke} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ color: axisStroke }} />
              {dataKeys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case "column":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis type="number" stroke={axisStroke} />
              <YAxis type="category" dataKey="name" stroke={axisStroke} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ color: axisStroke }} />
              {dataKeys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case "line":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis dataKey="name" stroke={axisStroke} />
              <YAxis stroke={axisStroke} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ color: axisStroke }} />
              {dataKeys.map((key, index) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={2}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case "pie":
      case "donut":
        const pieData = chartData.map((item, index) => ({
          name: item.name,
          value: item[dataKeys[0]] || 0,
          color: COLORS[index % COLORS.length],
        }));

        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) =>
                  `${name}: ${(percent * 100).toFixed(1)}%`
                }
                outerRadius={chartType === "donut" ? 120 : 140}
                innerRadius={chartType === "donut" ? 60 : 0}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        );

      case "funnel":
        // Simple funnel representation using bar chart
        const funnelData = [...chartData].sort(
          (a, b) => (b[dataKeys[0]] || 0) - (a[dataKeys[0]] || 0),
        );

        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={funnelData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis type="number" stroke={axisStroke} />
              <YAxis type="category" dataKey="name" stroke={axisStroke} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey={dataKeys[0]} fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return <div>Unsupported chart type</div>;
    }
  };

  return <div className="w-full">{renderChart()}</div>;
};

export default ReportChart;
